// @ts-check
/**
 * Zavaděč modulů progresivního vylepšení (R-4 analýzy 1B).
 *
 * Layout ho načítá jako `<script type="module" src="/static/<otisk>/js/app.js">`. Po spuštění
 * přidá na `<html>` třídu `js` a pro každý prvek `[data-module="<název>"]` naimportuje
 * `./<název>.js` a zavolá jeho `export default function init(root, { mount })` — právě jednou
 * (`mount` = kontext pro moduly, které vkládají obsah s vlastními kořeny; sekce 7 revize V2)
 * (`data-module-ready` se nastaví PŘED importem jako ochrana proti dvojímu startu). Teprve když
 * `init` doběhne bez výjimky (i async, po `await`), dostane kořen značku `data-enhanced`: CSS
 * pak skryje jeho záložní prvky `.js-hidden` (tlačítka „Zobrazit", „Použít vzhled"), které bez
 * modulu musí zůstat vidět — selhání importu nebo `init` by jinak nechalo mrtvý formulář
 * (revize K2b sekce 2). Značka platí jen pro NEJBLIŽŠÍ kořen modulu (selektor v app.css).
 * Spolu se značkou zapíše `performance.mark('etendry:enhanced:<název>')` (R-8 OP 0.3.3).
 *
 * Název modulu musí odpovídat `[a-z][a-z-]*`; jiný se tiše přeskočí (konzole zůstává čistá,
 * chybný atribut odhalí render test šablon). Modul pracuje jen uvnitř svého kořene.
 *
 * Importy jsou relativní, prohlížeč proto žádá moduly pod otiskem zavaděče: všechny soubory `js/`
 * nesou týž otisk stromu (R-17 analýzy 1C, `web/embed.go`). Po doběhnutí modulů — i když některý
 * selže — předá zavaděč fokus prvnímu `[data-autofocus]` (`lib/focus.js`, ui-checklist bod 5).
 *
 * Odkazy `a[data-dialog]` (sekce 7 R-10) nemají kořen modulu: zavaděč je obslouží jedním
 * posluchačem kliku (`dialogLinks`) a knihovnu `lib/dialog.js` naimportuje až při prvním kliku.
 */

import { autofocus } from './lib/focus.js';

const MODULE_NAME = /^[a-z][a-z-]*$/;

/**
 * @typedef {{ mount: (scope: ParentNode) => Promise<void> }} ModuleContext kontext modulu:
 *   `mount(scope)` oživí kořeny `data-module` v obsahu, který modul vložil (fragment s formuláři
 *   — zámek `form.js`), týmž importérem; vzor `lib/dialog.js` `open(link, { mount })`
 * @typedef {{ default?: (root: HTMLElement, ctx: ModuleContext) => unknown }} ModuleLike
 *   návratová hodnota init se ignoruje; slib se před značkou `data-enhanced` vyčká
 * @typedef {(name: string) => Promise<ModuleLike>} Importer
 */

/** @type {Importer} */
// eslint-disable-next-line no-unsanitized/method -- název prošel MODULE_NAME ([a-z][a-z-]*), cesta je vždy ./<název>.js
const defaultImporter = (name) => import(`./${name}.js`);

/**
 * Najde kořeny modulů v `scope` a inicializuje je. Vrací promise, která se vyřeší po inicializaci
 * všech nalezených modulů (testy a budoucí vložené fragmenty); selhání importu ji odmítne touž
 * chybou. Fokus na `[data-autofocus]` předá vždy až po modulech (`finally`).
 * @param {ParentNode} scope
 * @param {Importer} [importer] náhrada dynamického importu (testy)
 * @returns {Promise<void>}
 */
export async function mount(scope, importer = defaultImporter) {
  /** @type {Promise<void>[]} */
  const pending = [];
  /** @type {ModuleContext} */
  const ctx = { mount: (inner) => mount(inner, importer) };
  for (const el of scope.querySelectorAll('[data-module]')) {
    if (!(el instanceof HTMLElement) || el.hasAttribute('data-module-ready')) continue;
    const name = el.dataset.module ?? '';
    if (!MODULE_NAME.test(name)) continue;
    el.setAttribute('data-module-ready', '');
    pending.push(
      importer(name).then(async (mod) => {
        if (typeof mod.default !== 'function') return;
        // init smí být async: značka až po doběhnutí; odmítnutý slib = bez značky.
        await mod.default(el, ctx);
        el.setAttribute('data-enhanced', '');
        // Měřitelná značka oživení pro skript Lighthouse a E2E (FR-OP033-19).
        performance.mark(`etendry:enhanced:${name}`);
      }),
    );
  }
  try {
    await Promise.all(pending);
  } finally {
    autofocus(scope);
  }
}

/**
 * @typedef {{ open: (link: HTMLAnchorElement, options: { mount: typeof mount }) => Promise<void> }} DialogLib
 */

/**
 * Registrace odkazů `a[data-dialog]` (sekce 7 R-10): jeden posluchač `click` na dokumentu zachytí
 * levý klik bez modifikátorů (Ctrl, Meta, Shift, Alt a prostřední tlačítko = nová karta
 * prohlížeče), zruší navigaci a předá odkaz `open` knihovny `lib/dialog.js`, kterou naimportuje
 * až při prvním kliku — stránka bez dialogu ji nikdy nestáhne a modulepreload (kořeny
 * `data-module`) se nemění. Selhání importu pustí odkaz jako navigaci (bez JS platí stránka).
 * Delegace na dokumentu, protože odkazy stojí kdekoli (i ve vyměněných fragmentech výpisu)
 * a dialog je jeden sdílený v layoutu.
 * @param {Document} doc
 * @param {() => Promise<DialogLib>} [load] náhrada dynamického importu (testy)
 * @param {(url: string) => void} [navigate] náhrada `location.assign` (testy)
 * @returns {void}
 */
export function dialogLinks(
  doc,
  load = () => import('./lib/dialog.js'),
  navigate = (url) => doc.defaultView?.location.assign(url),
) {
  doc.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)
      return;
    const el = /** @type {Element | null} */ (e.target);
    const link = /** @type {HTMLAnchorElement | null} */ (
      el?.closest?.('a[data-dialog][href]') ?? null
    );
    if (link === null) return;
    e.preventDefault();
    load().then(
      (lib) => lib.open(link, { mount }),
      () => navigate(link.href),
    );
  });
}

/** Start v prohlížeči: modulové skripty běží až po rozparsování dokumentu. */
function boot() {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.add('js');
  dialogLinks(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void mount(document), { once: true });
  } else {
    void mount(document);
  }
}

boot();
